import { AxiosResponse } from 'axios';
import { OrderXML, StorageItem } from '../../../../apis/client-axios';
import { Input } from 'antd';
import { useLocation } from 'react-router-dom';
import { TDataRequest } from '../request';
import CustomSelect from '../../../../components/select/CustomSelect';
import { SvgArrowDownSelect } from '../../../../components/@svg/SvgArrowDownSelect';
import { CheckIcon } from '../@svg/MagnifyingGlassIcon/CheckIcon/CheckIcon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { decode as decodeHtml } from 'he';
import { roundToDecimal } from '../../../../util/function-util';
import { BatteryType } from '../../../../constants/battery';

type TProps = {
  orderEbay: AxiosResponse<OrderXML, any>;
  dataRequest: TDataRequest;
  setDataRequest: React.Dispatch<React.SetStateAction<TDataRequest>>;
  itemDetail: AxiosResponse<StorageItem, any>;
};

const SizePackage = ({ orderEbay, dataRequest, setDataRequest, itemDetail }: TProps) => {
  // Item chưa lưu kho không có 保管サイズ -> ĐỂ TRỐNG (chốt 2026-09-10: không hiển thị 0 do
  // roundToDecimal(null), cũng không hiển thị 到着時サイズ). Item có 保管サイズ hiển thị y như cũ.
  const packDim = (value?: number) => (value ? roundToDecimal(value, 0) : '');
  const { state } = useLocation();
  const IOSS = state['IOSS'];
  const [localItemPrice, setLocalItemPrice] = useState(dataRequest.itemPrice.toString());
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const mapOption: any = useMemo(
    () => [
      {
        value: BatteryType.NO_BATTERY,
        label: (
          <div className="option-pin">
            <span className="text">{BatteryType.NO_BATTERY}</span>
            {dataRequest?.battery.includes(BatteryType.NO_BATTERY) && <CheckIcon />}
          </div>
        ),
      },
      {
        value: BatteryType.BUILT_IN_LITHIUM_METAL,
        label: (
          <div className="option-pin">
            <span className="text">{BatteryType.BUILT_IN_LITHIUM_METAL}</span>
            {dataRequest?.battery.includes(BatteryType.BUILT_IN_LITHIUM_METAL) && <CheckIcon />}
          </div>
        ),
      },
      {
        value: BatteryType.PACKAGED_LITHIUM_METAL,
        label: (
          <div className="option-pin">
            <span className="text">{BatteryType.PACKAGED_LITHIUM_METAL}</span>
            {dataRequest?.battery.includes(BatteryType.PACKAGED_LITHIUM_METAL) && <CheckIcon />}
          </div>
        ),
      },
      {
        value: BatteryType.BUILT_IN_LITHIUM_ION,
        label: (
          <div className="option-pin">
            <span className="text">{BatteryType.BUILT_IN_LITHIUM_ION}</span>
            {dataRequest?.battery.includes(BatteryType.BUILT_IN_LITHIUM_ION) && <CheckIcon />}
          </div>
        ),
      },
      {
        value: BatteryType.PACKAGED_LITHIUM_ION,
        label: (
          <div className="option-pin">
            <span className="text">{BatteryType.PACKAGED_LITHIUM_ION}</span>
            {dataRequest?.battery.includes(BatteryType.PACKAGED_LITHIUM_ION) && <CheckIcon />}
          </div>
        ),
      },
    ],
    [dataRequest?.battery],
  );

  const debouncedUpdateShipmentData = useMemo(
    () =>
      debounce((value: string, inputName: string) => {
        setIsLoading(true);
        if (inputName === 'HSCode') {
          setDataRequest({
            ...dataRequest,
            errorInputHScode: value === '',
            HSCode: value,
            // carrier: '',
            // carrierPrice: 0,
            // shippingPlan: '',
            // shippingRate: '',
            // isInsurance: false,
          });
        } else if (inputName === 'ItemPrice') {
          const itemPrice = value === '' ? 0 : +value;
          setDataRequest({
            ...dataRequest,
            errorInputItemPrice: itemPrice <= 0,
            itemPrice: itemPrice,
            // carrier: '',
            // carrierPrice: 0,
            // shippingPlan: '',
            // shippingRate: '',
            // isInsurance: false,
            insurance: +((1.25 * itemPrice) / 100).toFixed(2),
          });
        }
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      }, 1000),
    [dataRequest, setDataRequest],
  );

  const updateShipmentData = useCallback(
    (value: string, inputName: string) => {
      debouncedUpdateShipmentData(value, inputName);
    },
    [debouncedUpdateShipmentData],
  );

  const handleInputChange = (value: string, inputName: string) => {
    if (inputName === 'HSCode') {
      setDataRequest({ ...dataRequest, HSCode: value.toString() });
    } else if (inputName === 'ItemPrice') {
      setLocalItemPrice(value);
      updateShipmentData(value, inputName);
    } else if (inputName === 'ItemName') {
      setDataRequest({ ...dataRequest, orderItemName: value.toString() });
    }
  };

  useEffect(() => {
    setLocalItemPrice(dataRequest.itemPrice.toString());
  }, [dataRequest.itemPrice, dataRequest.HSCode]);

  useEffect(() => {
    if (!dataRequest.battery) {
      setDataRequest({ ...dataRequest, battery: mapOption[0].value });
    }
  }, [dataRequest, setDataRequest, mapOption]);

  return (
    <div className="box row-size">
      <div className="title">
        <h3>パッケージ情報</h3>
        <span className="title-required">*</span>
      </div>
      <div className="list-info size-pack">
        <div className="item">
          <div className="item-left">
            <p className="item__label">商品情報</p>
          </div>
          <div className="item-mid">
            <Input
              type="text"
              value={decodeHtml(dataRequest.orderItemName)}
              onChange={(e) => handleInputChange(e.target.value, 'ItemName')}
              className="item-name"
            />
          </div>
          <div className="item-right">
            <p className="item__label">申告価格</p>
            <div className="hs-price is-price">
              <Input
                type="number"
                value={localItemPrice}
                onChange={(e) => handleInputChange(e.target.value, 'ItemPrice')}
                disabled={isLoading}
                style={{
                  width: `${Math.max(String(localItemPrice || '').length, 1)}ch`,
                }}
              />
              <span>USD</span>
            </div>
          </div>
        </div>
        <div className="item">
          <div className="item-left">
            <p className="item__label">リチウム電池</p>
          </div>
          <div className="item-mid">
            <CustomSelect
              suffixIcon={<SvgArrowDownSelect />}
              options={mapOption}
              value={dataRequest?.battery}
              onChange={(e: string) => {
                setDataRequest({ ...dataRequest, battery: e });
              }}
            />
          </div>
          {orderEbay?.data?.ShippingAddress?.Country !== 'US' && (
            <div className="item-right">
              <p className="item__label">HSコード</p>
              <div className="hs-price is-code">
                <Input value={dataRequest?.HSCode} onChange={(e) => handleInputChange(e.target.value, 'HSCode')} />
              </div>
            </div>
          )}
        </div>
        <div className="item item-diff-style">
          <div className="item-left">
            <p className="item__label">梱包サイズ（cm）</p>
          </div>
          <div className="item-mid">
            <div className="size">
              <div className="size__detail">
                <span>縦</span>
                <div className="number">
                  <span>{packDim(itemDetail?.data?.storageLength)}</span>
                </div>
              </div>
              <div className="size__detail">
                <span>横</span>
                <div className="number">
                  <span>{packDim(itemDetail?.data?.storageWidth)}</span>
                </div>
              </div>
              <div className="size__detail">
                <span>高さ</span>
                <div className="number">
                  <span>{packDim(itemDetail?.data?.storageHeight)}</span>
                </div>
              </div>
            </div>
            {IOSS && (
              <div className="size weight">
                <div className="size__detail">
                  <span>重量（kg）</span>
                  <div className="number">
                    <span>{packDim(itemDetail?.data?.storageWeight)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="item-right">
            {!IOSS ? (
              <div className="size weight">
                <div className="size__detail">
                  <span>重量（kg）</span>
                  <div className="number">
                    <span>{packDim(itemDetail?.data?.storageWeight)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <p className="item__label">IOSS</p>
                <div className="hs-price ioss">
                  <span>{IOSS}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SizePackage;
